<template>
  <div class="user-page" :class="device.desktop ? 'pa-6' : 'pa-0'">
    <v-row>
      <v-col cols="12">
        <v-card class="user-card animated animate__bounceIn elevation-0">
          <div class="user-toolbar">
            <div class="user-toolbar__left">
              <div class="user-toolbar__icon">
                <v-icon>mdi-account-cog-outline</v-icon>
              </div>
              <div>
                <div class="user-toolbar__title">Manajemen Pengguna</div>
                <div class="user-toolbar__sub">
                  {{ table.total || records.length || 0 }} pengguna terdaftar
                </div>
              </div>
            </div>

            <div class="user-toolbar__actions">
              <v-text-field
                v-model="table.options.search"
                append-icon="mdi-magnify"
                placeholder="Cari nama / email..."
                single-line
                hide-details
                dense
                outlined
                color="#1f7a57"
                class="user-search"
                clearable
              ></v-text-field>

              <v-tooltip bottom color="#0d4a36">
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    class="user-icon-btn"
                    v-on="on"
                    v-show="page.actions.refresh"
                    @click="fetchRecords"
                  >
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>Muat ulang</span>
              </v-tooltip>

              <v-btn
                class="user-btn-primary"
                v-show="page.actions.add"
                @click="openForm"
              >
                <v-icon left small>mdi-plus</v-icon>
                Tambah Pengguna
              </v-btn>
            </div>
          </div>

          <!-- Desktop -->
          <v-data-table
            v-show="device.desktop"
            :headers="headers"
            :items="records"
            :options.sync="table.options"
            :server-items-length="table.total"
            class="user-table"
            :loading="loading.table"
            loading-text="Memuat data pengguna..."
            hide-default-footer
          >
            <v-progress-linear
              slot="progress"
              color="#1f7a57"
              height="2"
              indeterminate
            ></v-progress-linear>

            <template v-slot:item.name="{ item }">
              <div class="user-name-cell">
                <div class="user-name-cell__mark">
                  {{ (item.name || 'U').charAt(0).toUpperCase() }}
                </div>
                <div>
                  <div class="user-name-cell__title">{{ item.name }}</div>
                  <div class="user-name-cell__hint">{{ item.email }}</div>
                </div>
              </div>
            </template>

            <template v-slot:item.authent="{ value }">
              <span class="user-chip" :class="levelClass(value)">
                {{ levelLabel(value) }}
              </span>
            </template>

            <template v-slot:item.paket_uuid="{ item }">
              <div class="user-paket-cell">
                <span class="user-paket">{{ paketName(item.paket_uuid) }}</span>
                <span
                  v-if="item.paket_uuid && item.paket_expire_at"
                  class="user-expire"
                  :class="{ 'is-expired': isExpirePast(item.paket_expire_at) }"
                >
                  s/d {{ formatExpire(item.paket_expire_at) }}
                </span>
                <span
                  v-else-if="item.paket_uuid"
                  class="user-expire user-expire--muted"
                >
                  tanpa batas
                </span>
              </div>
            </template>

            <template v-slot:item.status="{ value }">
              <span
                class="user-status"
                :class="value ? 'is-on' : 'is-off'"
              >
                <span class="user-status__dot"></span>
                {{ value ? 'Aktif' : 'Nonaktif' }}
              </span>
            </template>

            <template v-slot:item.id="{ value }">
              <div class="user-row-actions">
                <v-tooltip bottom color="#0d4a36">
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      small
                      class="user-action-edit"
                      v-on="on"
                      v-show="page.actions.edit"
                      @click="editRecord(value)"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Ubah</span>
                </v-tooltip>
                <v-tooltip bottom color="#0d4a36">
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      small
                      class="user-action-delete"
                      v-on="on"
                      v-show="page.actions.delete"
                      @click="postDeleteRecord(value)"
                    >
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Hapus</span>
                </v-tooltip>
              </div>
            </template>

            <template v-slot:no-data>
              <div class="user-empty">
                <v-icon large color="#8fd6b5">mdi-account-outline</v-icon>
                <div class="user-empty__title">Belum ada pengguna</div>
                <div class="user-empty__sub">Tambahkan pengguna pertama</div>
              </div>
            </template>
          </v-data-table>

          <!-- Mobile -->
          <div class="user-mobile" v-show="device.mobile">
            <div
              v-for="item in filterItems"
              :key="item.id"
              class="user-mobile-card"
            >
              <div class="user-mobile-card__top">
                <div class="user-name-cell">
                  <div class="user-name-cell__mark">
                    {{ (item.name || 'U').charAt(0).toUpperCase() }}
                  </div>
                  <div>
                    <div class="user-name-cell__title">{{ item.name }}</div>
                    <div class="user-name-cell__hint">{{ item.email }}</div>
                  </div>
                </div>
                <v-menu bottom left origin="center center" transition="scale-transition">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" class="user-icon-btn">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense class="user-menu">
                    <v-list-item
                      @click="editRecord(item.id)"
                      v-show="page.actions.edit"
                    >
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="#1f7a57">mdi-pencil</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Ubah Data</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      @click="postDeleteRecord(item.id)"
                      v-show="page.actions.delete"
                    >
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="#c62828">mdi-delete-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Hapus Data</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
              <div class="user-mobile-card__meta">
                <span class="user-chip" :class="levelClass(item.authent)">
                  {{ levelLabel(item.authent) }}
                </span>
                <span
                  class="user-status"
                  :class="item.status ? 'is-on' : 'is-off'"
                >
                  {{ item.status ? 'Aktif' : 'Nonaktif' }}
                </span>
                <span class="user-paket">{{ paketName(item.paket_uuid) }}</span>
                <span
                  v-if="item.paket_uuid && item.paket_expire_at"
                  class="user-expire"
                  :class="{ 'is-expired': isExpirePast(item.paket_expire_at) }"
                >
                  s/d {{ formatExpire(item.paket_expire_at) }}
                </span>
              </div>
            </div>

            <div v-if="!filterItems.length" class="user-empty pa-8">
              <v-icon large color="#8fd6b5">mdi-account-outline</v-icon>
              <div class="user-empty__title">Belum ada pengguna</div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog
      transition="dialog-bottom-transition"
      v-model="form.add"
      :max-width="device.desktop ? '560px' : '100%'"
      persistent
      :fullscreen="device.mobile"
      content-class="user-dialog"
    >
      <v-card class="user-dialog-card">
        <div class="user-dialog-header">
          <div class="user-dialog-header__copy">
            <v-icon class="mr-2">mdi-account-cog-outline</v-icon>
            {{ form.edit ? 'Ubah Pengguna' : 'Tambah Pengguna' }}
          </div>
          <v-btn icon dark small @click="closeForm">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="user-dialog-body">
          <div class="user-field-label">Nama Pengguna</div>
          <v-text-field
            outlined
            dense
            hide-details="auto"
            color="#1f7a57"
            placeholder="Nama lengkap"
            v-model="record.name"
            class="user-field mb-4"
            prepend-inner-icon="mdi-account-outline"
            autocomplete="off"
          ></v-text-field>

          <div class="user-field-label">Email</div>
          <v-text-field
            outlined
            dense
            hide-details="auto"
            color="#1f7a57"
            type="email"
            placeholder="email@contoh.com"
            v-model="record.email"
            class="user-field mb-4"
            prepend-inner-icon="mdi-email-outline"
            autocomplete="off"
          ></v-text-field>

          <div class="user-field-label">Level</div>
          <v-select
            outlined
            dense
            hide-details="auto"
            color="#1f7a57"
            item-color="#1f7a57"
            v-model="record.authent"
            :items="authents"
            class="user-field mb-4"
            prepend-inner-icon="mdi-shield-account-outline"
          ></v-select>

          <div class="user-field-label">Paket Harga</div>
          <v-select
            outlined
            dense
            hide-details="auto"
            color="#1f7a57"
            item-color="#1f7a57"
            v-model="record.paket_uuid"
            :items="paketItems"
            item-text="name"
            item-value="id"
            clearable
            placeholder="Pilih paket (opsional)"
            class="user-field mb-4"
            prepend-inner-icon="mdi-package-variant-closed"
            @change="onPaketChange"
          ></v-select>

          <template v-if="record.paket_uuid">
            <div class="user-field-label">
              Berlaku sampai
              <span class="user-field-hint">(sebelum pembayaran berikutnya)</span>
            </div>
            <v-text-field
              v-model="record.paket_expire_at"
              type="date"
              outlined
              dense
              hide-details="auto"
              color="#1f7a57"
              class="user-field mb-4"
              prepend-inner-icon="mdi-calendar-end"
              clearable
            ></v-text-field>
          </template>

          <div class="user-switches">
            <v-switch
              v-model="record.status"
              label="Akun aktif"
              color="#1f7a57"
              hide-details
              dense
              class="mt-0"
            ></v-switch>
            <v-switch
              v-if="form.edit"
              v-model="record.reset"
              label="Reset kata sandi ke bawaan"
              color="#1f7a57"
              hide-details
              dense
              class="mt-0"
            ></v-switch>
          </div>

          <div class="user-tips">
            <v-icon small color="#1f7a57" class="mr-2">mdi-key-variant</v-icon>
            Kata sandi bawaan untuk pengguna baru: <strong>12345678</strong>
          </div>
        </v-card-text>

        <v-divider class="user-divider"></v-divider>
        <v-card-actions class="user-dialog-actions">
          <v-btn text class="user-btn-ghost" @click="closeForm">Batal</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            class="user-btn-primary"
            v-show="!form.edit"
            @click="postAddNewRecord"
          >
            <v-icon left small>mdi-plus</v-icon>
            Tambah
          </v-btn>
          <v-btn
            class="user-btn-primary"
            v-show="form.edit"
            @click="postUpdateRecord"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Simpan
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import "animate.css";

export default {
  name: "ManajemenPengguna",
  data: () => ({
    headers: [
      { text: "Pengguna", align: "start", sortable: true, value: "name" },
      { text: "Level", value: "authent", sortable: true, width: 140 },
      { text: "Paket", value: "paket_uuid", sortable: false },
      { text: "Status", value: "status", sortable: true, width: 120 },
      {
        text: "Aksi",
        value: "id",
        width: 110,
        sortable: false,
        align: "center",
      },
    ],
    authents: [
      { text: "Administrator", value: "administrator" },
      { text: "Customer", value: "customer" },
    ],
    paketItems: [],
  }),
  computed: {
    ...mapState([
      "page",
      "theme",
      "http",
      "device",
      "record",
      "records",
      "loading",
      "form",
      "table",
      "snackbar",
    ]),
    filterItems() {
      const q = (this.table.options.search || "").toLowerCase();
      if (!q) return this.records || [];
      return (this.records || []).filter((item) => {
        const name = (item.name || "").toLowerCase();
        const email = (item.email || "").toLowerCase();
        return name.indexOf(q) > -1 || email.indexOf(q) > -1;
      });
    },
  },
  created() {
    this.setPage({
      crud: true,
      dataUrl: "api/utility/users",
      pagination: true,
      title: "MANAJEMEN PENGGUNA",
      subtitle: "Kelola akun pengguna aplikasi",
      breadcrumbs: [
        { text: "Utility", disabled: true, href: "" },
        { text: "Manajemen Pengguna", disabled: false, href: "" },
      ],
      actions: {
        refresh: true,
        add: true,
        edit: true,
        delete: true,
      },
    });
  },
  mounted() {
    this.fetchPakets();
  },
  methods: {
    ...mapActions([
      "setPage",
      "setForm",
      "setRecord",
      "fetchRecords",
      "postAddNew",
      "postEdit",
      "postUpdate",
      "postConfirmDelete",
    ]),
    levelLabel(value) {
      if (value === "administrator") return "Administrator";
      if (value === "customer") return "Customer";
      return value || "—";
    },
    levelClass(value) {
      if (value === "administrator") return "is-admin";
      return "is-customer";
    },
    paketName(uuid) {
      if (!uuid) return "—";
      const found = (this.paketItems || []).find(
        (p) => p.id === uuid || p.uuid === uuid
      );
      return (found && found.name) || "—";
    },
    formatExpire(value) {
      if (!value) return "—";
      const raw = String(value).slice(0, 10);
      const parts = raw.split("-");
      if (parts.length !== 3) return raw;
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    },
    isExpirePast(value) {
      if (!value) return false;
      const end = new Date(String(value).slice(0, 10) + "T23:59:59");
      return Date.now() > end.getTime();
    },
    onPaketChange(val) {
      if (!val) {
        this.$set(this.record, "paket_expire_at", null);
      }
    },
    async fetchPakets() {
      try {
        const { data } = await this.http.get(
          "api/superadmin/master-data/paket"
        );
        this.paketItems = Array.isArray(data) ? data : [];
      } catch (e) {
        this.paketItems = [];
      }
    },
    openForm() {
      this.setForm({ add: true, edit: false });
      this.setRecord({
        name: "",
        email: "",
        authent: "customer",
        paket_uuid: null,
        paket_expire_at: null,
        status: true,
        reset: false,
      });
    },
    closeForm() {
      this.setForm({ add: false, edit: false });
    },
    postAddNewRecord() {
      this.postAddNew(this.record).then(() => {
        this.fetchRecords();
        this.closeForm();
      });
    },
    editRecord(val) {
      this.postEdit(val).then(() => {
        this.setForm({ add: true, edit: true });
      });
    },
    postUpdateRecord() {
      this.postUpdate(this.record).then(() => {
        this.fetchRecords();
        this.closeForm();
      });
    },
    postDeleteRecord(val) {
      this.postConfirmDelete(val);
    },
  },
  watch: {
    "table.options": {
      handler() {
        if (!this.form.edit) this.fetchRecords();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.user-page {
  --gw-ink: #10241c;
  --gw-forest: #0d4a36;
  --gw-leaf: #1f7a57;
  --gw-mint: #8fd6b5;
  --gw-fog: #eef6f1;
  --gw-line: #c9ddd3;
  --gw-muted: #5d7268;
}

.user-card {
  border-radius: 14px !important;
  overflow: hidden;
  border: 1px solid var(--gw-line);
  background: #fff !important;
}

.user-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 1rem 1.15rem;
  background:
    radial-gradient(120% 80% at 0% 0%, #1a6b4d 0%, transparent 55%),
    linear-gradient(175deg, #0b3d2e 0%, #0f5c45 45%, #0a3327 100%);
  color: #f4fbf7;
}

.user-toolbar__left {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}

.user-toolbar__icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, #b7ebcf, #8fd6b5);
}

.user-toolbar__icon .v-icon {
  color: #0b3d2e !important;
}

.user-toolbar__title {
  font-family: "Outfit", "Montserrat", sans-serif;
  font-weight: 700;
  font-size: 1.05rem;
}

.user-toolbar__sub {
  margin-top: 0.15rem;
  font-size: 0.75rem;
  color: rgba(244, 251, 247, 0.65);
}

.user-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.user-search {
  max-width: 240px;
}

.user-search >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.1) !important;
  border-radius: 10px !important;
}

.user-search >>> input,
.user-search >>> .v-icon {
  color: #f4fbf7 !important;
}

.user-icon-btn {
  color: #f4fbf7 !important;
}

.user-btn-primary {
  background: linear-gradient(145deg, #b7ebcf, #8fd6b5) !important;
  color: #0b3d2e !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 700 !important;
  border-radius: 10px !important;
  box-shadow: none !important;
}

.user-btn-ghost {
  text-transform: none !important;
  letter-spacing: 0 !important;
  color: var(--gw-leaf) !important;
  font-weight: 700 !important;
}

.user-table {
  background: transparent !important;
}

.user-table >>> thead th {
  background: var(--gw-fog) !important;
  color: var(--gw-forest) !important;
  font-weight: 700 !important;
  font-size: 0.75rem !important;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-bottom: 1px solid var(--gw-line) !important;
}

.user-table >>> tbody td {
  border-bottom: 1px solid var(--gw-line) !important;
  color: var(--gw-ink);
  height: 64px !important;
}

.user-name-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.user-name-cell__mark {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  font-family: "Outfit", "Montserrat", sans-serif;
  font-weight: 700;
  color: #0b3d2e;
  background: linear-gradient(145deg, #b7ebcf, #8fd6b5);
}

.user-name-cell__title {
  font-weight: 600;
  color: var(--gw-ink);
  line-height: 1.2;
}

.user-name-cell__hint {
  margin-top: 0.15rem;
  font-size: 0.72rem;
  color: var(--gw-muted);
}

.user-chip {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.user-chip.is-admin {
  background: rgba(31, 122, 87, 0.16);
  color: var(--gw-forest);
}

.user-chip.is-customer {
  background: #eef2f6;
  color: #455a64;
}

.user-paket-cell {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.user-paket {
  font-size: 0.85rem;
  color: var(--gw-muted);
  font-weight: 600;
}

.user-expire {
  font-size: 0.72rem;
  font-weight: 600;
  color: #1f7a57;
}

.user-expire.is-expired {
  color: #c62828;
}

.user-expire--muted {
  color: #8aa097;
  font-weight: 500;
}

.user-field-hint {
  font-weight: 500;
  color: #8aa097;
  font-size: 0.72rem;
}

.user-status {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  font-weight: 700;
}

.user-status__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

.user-status.is-on {
  color: #1f7a57;
}

.user-status.is-off {
  color: #9e9e9e;
}

.user-row-actions {
  display: flex;
  justify-content: center;
  gap: 0.15rem;
}

.user-action-edit {
  color: var(--gw-leaf) !important;
}

.user-action-delete {
  color: #c62828 !important;
}

.user-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  text-align: center;
}

.user-empty__title {
  margin-top: 0.75rem;
  font-family: "Outfit", sans-serif;
  font-weight: 700;
  color: var(--gw-ink);
}

.user-empty__sub {
  margin-top: 0.25rem;
  font-size: 0.85rem;
  color: var(--gw-muted);
}

.user-mobile {
  padding: 0.75rem;
  display: grid;
  gap: 0.75rem;
}

.user-mobile-card {
  border: 1px solid var(--gw-line);
  border-radius: 12px;
  padding: 0.85rem;
  background: #fff;
}

.user-mobile-card__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
}

.user-mobile-card__meta {
  margin-top: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  align-items: center;
}

.user-menu {
  border-radius: 10px !important;
}

.user-dialog-card {
  border-radius: 14px !important;
  overflow: hidden;
}

.user-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.9rem 1rem;
  background:
    radial-gradient(120% 80% at 0% 0%, #1a6b4d 0%, transparent 55%),
    linear-gradient(175deg, #0b3d2e 0%, #0f5c45 45%, #0a3327 100%);
  color: #f4fbf7;
}

.user-dialog-header__copy {
  display: flex;
  align-items: center;
  font-family: "Outfit", sans-serif;
  font-weight: 700;
}

.user-dialog-body {
  padding: 1.25rem 1.25rem 0.5rem !important;
}

.user-field-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--gw-forest);
  margin-bottom: 0.35rem;
}

.user-field >>> .v-input__slot {
  border-radius: 10px !important;
}

.user-switches {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  background: var(--gw-fog);
  border: 1px solid var(--gw-line);
  margin-bottom: 1rem;
}

.user-tips {
  display: flex;
  align-items: flex-start;
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  background: var(--gw-fog);
  border: 1px solid var(--gw-line);
  color: var(--gw-muted);
  font-size: 0.82rem;
  line-height: 1.45;
  margin-bottom: 0.5rem;
}

.user-divider {
  border-color: var(--gw-line) !important;
}

.user-dialog-actions {
  padding: 0.85rem 1.15rem 1.1rem !important;
}

@media (max-width: 600px) {
  .user-toolbar__actions {
    width: 100%;
  }
  .user-search {
    max-width: none;
    flex: 1;
  }
}
</style>
